import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Crown, Check, Shield, Lock, Zap, ArrowRight, Clock, CreditCard, Store, X, Star, Award } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { cn } from "@/lib/utils";
import toast from 'react-hot-toast';
import axios from 'axios';
import { motion } from "framer-motion";
import { trackPixelEvent, PIXEL_EVENTS } from '../utils/pixelService.js';
import { trackPlanViewed, trackBeginCheckout, trackAddPaymentInfo } from '../utils/gaService.js';
import { trackHotjarEvent } from '../utils/hotjarService.js';

const PLAN_CONFIGS = {
  Basic: {
    name: "Básico",
    price: "49",
    icon: Zap,
    description: "Para dueños de una mascota",
    subtitle: "Ideal para monitoreo mensual básico",
    features: [
      "5 análisis al mes",
      "Evaluación detallada de salud visible",
      "Guía de cuidados y alimentación",
      "Información completa de su raza",
      "Acceso a veterinarios online", 
      "Historial de análisis"
    ],
    color: "#4361ee",
    gradient: "linear-gradient(135deg, #4361ee 0%, #4cc9f0 100%)"
  },
  Pro: {
    name: "Pro",
    price: "99",
    icon: Shield,
    description: "Para familias con 2-3 mascotas",
    subtitle: "Ahorra 67% vs plan básico por mascota",
    features: [
      "15 análisis al mes",
      "Evaluación detallada de salud visible",
      "Guía de cuidados y alimentación",
      "Información completa de su raza",
      "Atención veterinaria prioritaria",
      "Historial de análisis"
    ],
    color: "#f8961e",
    gradient: "linear-gradient(135deg, #f8961e 0%, #f9c74f 100%)",
    recommended: true
  },
  Premium: {
    name: "Premium",
    price: "149",
    icon: Crown,
    description: "Para refugios, albergues, veterinarios",
    subtitle: "Con análisis ilimitados y atención 24/7",
    features: [
      "Análisis ilimitados",
      "Evaluación detallada de salud visible",
      "Guía de cuidados y alimentación",
      "Información completa de su raza",
      "Atención veterinaria 24/7",
      "Historial de análisis"
    ],
    color: "#9d4edd",
    gradient: "linear-gradient(135deg, #9d4edd 0%, #c77dff 100%)"
  }
};

// Customer success stories - builds trust
const SUCCESS_STORIES = [
  { name: "Carlos R.", image: "/avatars/carlos.jpg", text: "Detectamos un problema de salud en Luna a tiempo gracias a los análisis mensuales." },
  { name: "María G.", image: "/avatars/maria.jpg", text: "La atención veterinaria prioritaria ayudó a mis 3 perros en varias emergencias." },
  { name: "Alejandra T.", image: "/avatars/alejandra.jpg", text: "Como veterinaria, recomiendo esta plataforma a todos mis clientes." }
];

// Payment method options
const PAYMENT_METHODS = [
  { id: "card", name: "Tarjeta Digital", icon: CreditCard },
  { id: "oxxo", name: "Efectivo en OXXO", icon: Store }
];

// Trustmarks to display in the footer
const TRUST_MARKS = [
  { icon: Lock, text: "Pago 100% seguro", color: "text-green-600" },
  { icon: Shield, text: "SSL Certificado", color: "text-blue-600" },
  { icon: Clock, text: "Activación inmediata", color: "text-amber-600" }
];

const PricingModal = ({ isOpen, onClose, onSelectPlan, isLoading, plan = 'Free' }) => {
  const { user } = useUser();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("card");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [isMobile, setIsMobile] = useState(false);
  const [activePlan, setActivePlan] = useState("Pro");
  const [currentStory, setCurrentStory] = useState(0);
  const [showComparison, setShowComparison] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    // Reset tracking flag when modal closes
    if (!isOpen) {
      hasTrackedView.current = false;
      return;
    }
    
    // Track plan viewed event only once when modal opens
    if (isOpen && !hasTrackedView.current) {
      // Parámetros comunes para el evento de visualización de planes
      const planViewParams = {
        content_name: 'Subscription Plans',
        content_category: 'pricing',
        source: 'upgrade_modal',
        current_plan: plan
      };
      
      // Rastrear en Facebook Pixel
      trackPixelEvent(PIXEL_EVENTS.PLAN_VIEWED, planViewParams);
      
      // Rastrear en Google Analytics
      trackPlanViewed('upgrade_modal', planViewParams);
      
      // Rastrear en Hotjar
      trackHotjarEvent('pricing_modal_viewed');
      
      // Set flag to prevent multiple triggers
      hasTrackedView.current = true;
    }
  }, [isOpen, plan]);

  useEffect(() => {
    setIsPageLoaded(true);
    
    // Check if mobile for responsive layout
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const checkMobile = () => setIsMobile(window.innerWidth < 768);

  // Auto-rotate success stories
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStory(prev => (prev + 1) % SUCCESS_STORIES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Calculate annual pricing (20% discount)
  const getPrice = (basePrice) => {
    if (billingCycle === "annual") {
      const monthlyPrice = parseInt(basePrice);
      const annualPrice = (monthlyPrice * 12 * 0.8).toFixed(0);
      return {
        value: annualPrice,
        perMonth: (monthlyPrice * 0.8).toFixed(0),
        savings: (monthlyPrice * 12 * 0.2).toFixed(0)
      };
    }
    return { value: basePrice, perMonth: basePrice, savings: 0 };
  };

  const handleSubscriptionManagement = async () => {
    const loadingToast = toast.loading('Procesando tu solicitud...');
    try {
      const response = await axios.post('/api/create-portal-session', {
        userEmail: user?.primaryEmailAddress?.emailAddress,
        userId: user?.id
      });

      if (response.data?.url) {
        toast.dismiss(loadingToast);
        toast.success('Redirigiendo al portal de gestión...');
        setTimeout(() => window.location.assign(response.data.url), 1000);
      }
    } catch (error) {
      console.error('Portal session error:', error);
      toast.dismiss(loadingToast);
      toast.error('No se pudo abrir el portal de facturación. Por favor intenta de nuevo.');
    }
  };

  const handlePlanSelection = async (planConfig, paymentMethod) => {
    // Track checkout initiated event
    const planName = planConfig.name;
    const planPrice = parseInt(planConfig.price);
    
    // Parámetros comunes para el evento de inicio de pago
    const checkoutParams = {
      content_name: `Plan: ${planName}`,
      content_type: 'subscription',
      content_ids: [planName],
      value: planPrice,
      currency: 'MXN'
    };
    
    // Rastrear en Facebook Pixel
    trackPixelEvent(PIXEL_EVENTS.CHECKOUT_INITIATED, checkoutParams);
    
    // Rastrear en Google Analytics - Begin Checkout
    trackBeginCheckout(planName, planPrice, 'MXN', checkoutParams);
    
    // Rastrear en Hotjar
    trackHotjarEvent(`checkout_started_${planName.toLowerCase()}`);
    
    try {
      // Mostrar toast de carga
      const loadingToast = toast.loading('Procesando tu solicitud...');
      
      // Datos comunes para todos los métodos de pago
      const paymentData = {
        userId: user?.id,
        userEmail: user?.primaryEmailAddress?.emailAddress,
        name: planConfig.name,
        paymentMethod,
        billingCycle
      };
      
      // Rastrear la información de pago seleccionada
      trackAddPaymentInfo(planName, planPrice, paymentMethod, 'MXN', {
        billing_cycle: billingCycle
      });
      
      // Rastrear método de pago en Hotjar
      trackHotjarEvent(`payment_method_${paymentMethod}`);
      
      // Realizar la solicitud al endpoint de checkout
      const response = await axios.post('/api/checkout', paymentData);
      
      // Si tenemos una URL de redirección, proceder
      if (response.data?.url) {
        toast.dismiss(loadingToast);
        
        // Mensaje específico según el método de pago
        const successMessage = paymentMethod === 'oxxo' 
          ? 'Generando código OXXO...' 
          : 'Redirigiendo al portal de pago...';
        
        toast.success(successMessage);
        
        // Redirigir al usuario a la URL de checkout con un pequeño retraso
        // para que pueda ver el mensaje de éxito
        setTimeout(() => window.location.assign(response.data.url), 1000);
      } else {
        // Si no hay URL, mostrar error
        toast.dismiss(loadingToast);
        toast.error('Error al procesar el pago. Por favor intenta de nuevo.');
      }
    } catch (error) {
      // Manejar errores
      toast.dismiss(loadingToast);
      console.error('Payment error:', error);
      
      // Mensaje de error personalizado si está disponible
      const errorMessage = error.response?.data?.error || 'Error al procesar el pago. Por favor intenta de nuevo.';
      toast.error(errorMessage);
      
      // Registrar detalles del error para depuración
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`
          w-full
          max-w-[98vw]
          md:max-w-[92vw]
          lg:max-w-[88vw]
          xl:max-w-[1400px]
          p-0
          border-0
          shadow-2xl
          bg-white
          rounded-2xl
          m-0
          h-auto
          max-h-[98vh]
          overflow-auto
          ${isMobile ? 'mobile-pricing-modal' : ''}
          pt-12 sm:pt-0
        `}
        style={{
          '--card-shadow': '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Close button - Repositioned to be more visible on small screens */}
        <button 
          onClick={onClose}
          className="fixed right-4 top-20 sm:top-4 z-[100] w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-gray-100 transition-colors shadow-lg border border-gray-200"
          aria-label="Cerrar ventana"
          style={{
            backdropFilter: 'blur(4px)'
          }}
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>

        <div className="flex flex-col relative">
          {/* Header Section with subtle gradient background */}
          <div className="px-6 py-3 text-center border-b border-gray-100 bg-gradient-to-b from-amber-50 to-white">
            <div className="inline-flex items-center gap-2 bg-amber-50 px-3 py-1 rounded-full mb-2 border border-amber-100 shadow-sm">
              <Award className="h-4 w-4 text-amber-600" />
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-amber-800`}>
                #1 Plataforma para cuidado de mascotas en México
              </span>
            </div>

            <h2 className={`${isMobile ? 'text-xl' : 'text-2xl sm:text-3xl'} font-bold text-gray-900 mb-1`}>
              Cuida la salud de tu mascota de manera inteligente
            </h2>
            
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 max-w-xl mx-auto mb-0`}>
              Monitorea la salud de tu mascota con tecnología avanzada y recibe atención veterinaria cuando la necesites
            </p>
          </div>

          {/* Success Story Banner - Builds trust and social proof */}
          <div className="bg-blue-50 border-y border-blue-100 px-4 py-1 hidden sm:block">
            <motion.div 
              className="flex items-center justify-center gap-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              key={currentStory}
              transition={{ duration: 0.5 }}
            >
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                <Star className="h-3 w-3 text-yellow-500" fill="#EAB308" />
              </div>
              <p className="text-xs text-blue-800 font-medium italic">
                "{SUCCESS_STORIES[currentStory].text}"
              </p>
              <span className="text-xs font-semibold text-blue-900">— {SUCCESS_STORIES[currentStory].name}</span>
            </motion.div>
          </div>

          {/* Mobile Plan Tabs - Only visible on mobile */}
          {isMobile && (
            <div className="px-4 py-2 bg-white sticky top-0 z-20 shadow-sm">
              <div className="flex bg-gray-100 p-1 rounded-full shadow-sm">
                {Object.entries(PLAN_CONFIGS).map(([planKey, planConfig]) => (
                  <button
                    key={`tab-${planKey}`}
                    onClick={() => setActivePlan(planKey)}
                    className={`
                      flex-1 py-1.5 px-2 rounded-full text-xs font-medium transition-all
                      ${activePlan === planKey ? "bg-white shadow text-gray-900" : "text-gray-600"}
                    `}
                  >
                    <span>{planConfig.name}</span>
                    {planConfig.recommended && (
                      <div className="absolute -top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Billing Cycle Toggle - Desktop only */}
         

          {/* Plan Cards - Enhanced with subtle shadows and modern styling */}
          <div className="px-6 py-2">
            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3'} gap-5`}>
              {Object.entries(PLAN_CONFIGS).map(([planKey, planConfig]) => {
                const isCurrent = planKey === plan;
                const price = getPrice(planConfig.price);
                
                if (isMobile && activePlan !== planKey) return null;
                
                return (
                  <motion.div
                    key={planKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: planKey === "Basic" ? 0 : planKey === "Pro" ? 0.1 : 0.2 }}
                    className={`
                      bg-white rounded-xl relative transition-all duration-200 hover:shadow-lg
                      ${planConfig.recommended 
                        ? "border-2 border-amber-300 shadow-md" 
                        : "border border-gray-200 shadow-sm hover:border-gray-300"}
                      ${isCurrent && "opacity-90"}
                      ${!isMobile && "transform hover:-translate-y-1"}
                    `}
                    style={{ boxShadow: 'var(--card-shadow)' }}
                  >
                    {/* Popular Badge */}
                    {planConfig.recommended && (
                      <div className="absolute -top-3 right-0 left-0 flex justify-center">
                        <div className="bg-gradient-to-r from-amber-500 to-amber-400 text-white text-xs px-3 py-0.5 rounded-full font-medium shadow-sm">
                          Más Popular
                        </div>
                      </div>
                    )}
                    
                    {/* Plan Header */}
                    <div className="p-3 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white">
                      <div className="flex items-center gap-2 mb-2">
                        <div 
                          className="p-2 rounded-lg shadow-sm"
                          style={{ background: `${planConfig.color}15` }}
                        >
                          <planConfig.icon className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} style={{ color: planConfig.color }} />
                        </div>
                        <div>
                          <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-bold text-gray-900`}>{planConfig.name}</h3>
                          <p className="text-xs text-gray-600">{planConfig.description}</p>
                        </div>
                      </div>
                      
                      {/* Price Display */}
                      <div className="flex items-baseline">
                        <span className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900`}>${price.value}</span>
                        <span className="ml-1 text-gray-500 text-xs">MXN/{billingCycle === "monthly" ? "mes" : "año"}</span>
                      </div>
                      
                      {/* Annual savings highlight */}
                      {billingCycle === "annual" && (
                        <div className="flex items-center mt-1">
                          <span className="text-xs text-gray-600">${price.perMonth} al mes</span>
                          <span className="ml-2 text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">
                            Ahorras ${price.savings}
                          </span>
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-1">{planConfig.subtitle}</p>
                    </div>
                    
                    {/* Features List - Better organized with colored checkmarks */}
                    <div className="p-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">INCLUYE:</h4>
                      <ul className="space-y-1.5">
                        {planConfig.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <div className="rounded-full p-0.5 mt-0.5" style={{ backgroundColor: `${planConfig.color}20` }}>
                              <Check className="h-3 w-3" style={{ color: planConfig.color }} />
                            </div>
                            <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Action Button - Gradient background for premium feel */}
                    <div className="p-3 border-t border-gray-100 bg-gray-50">
                      {!isCurrent ? (
                        <button
                          onClick={() => handlePlanSelection(planConfig, selectedPaymentMethod)}
                          disabled={isLoading}
                          className={`w-full ${isMobile ? 'py-2' : 'py-3'} rounded-lg text-white font-medium transition-all hover:shadow-md flex items-center justify-center gap-2`}
                          style={{ 
                            background: planConfig.gradient,
                            opacity: isLoading ? 0.7 : 1
                          }}
                        >
                          {isLoading ? 'Procesando...' : 'Seleccionar Plan'}
                          {!isLoading && <ArrowRight className="h-3 w-3" />}
                        </button>
                      ) : (
                        <button
                          disabled
                          className={`w-full ${isMobile ? 'py-2' : 'py-3'} rounded-lg bg-gray-200 text-gray-600 font-medium cursor-not-allowed flex items-center justify-center gap-2`}
                        >
                          <Check className="h-3 w-3" />
                          <span>Plan Actual</span>
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Compare Plans Button */}
            <div className="flex justify-center mt-2">
              <button 
                onClick={() => setShowComparison(!showComparison)}
                className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1`}
              >
                {showComparison ? "Ocultar comparación" : "Comparar planes en detalle"}
                <span className="text-xs">{showComparison ? "▲" : "▼"}</span>
              </button>
            </div>

            {/* Plan Comparison Table - Canva style */}
            {showComparison && (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className={`py-2 px-2 text-left ${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-gray-600`}>Característica</th>
                      {Object.entries(PLAN_CONFIGS).map(([planKey, planConfig]) => (
                        <th key={planKey} className="py-2 px-2 text-center">
                          <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold`} style={{ color: planConfig.color }}>
                            {planConfig.name}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className={`py-1.5 px-2 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>Análisis mensuales</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>5</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>15</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>Ilimitados</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className={`py-1.5 px-2 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>Atención veterinaria</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>Básica</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>Prioritaria</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>24/7</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className={`py-1.5 px-2 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>Mascotas incluidas</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>1</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>3-5</td>
                      <td className={`py-1.5 px-2 text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>Ilimitadas</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payment Methods and Trust Elements - Styled like premium platforms */}
          <div className="px-6 py-2 border-t border-gray-100 bg-gray-50">
            {/* Payment Method Selector - Redesigned for clarity */}
            <div className="flex flex-col sm:flex-row justify-between gap-2 items-center mb-2">
              <div className="flex bg-white p-1 rounded-lg border border-gray-200 shadow-sm w-full sm:w-auto">
                {PAYMENT_METHODS.map(method => (
                  <button 
                    key={method.id}
                    onClick={() => setSelectedPaymentMethod(method.id)}
                    className={`
                      flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-1 rounded-md ${isMobile ? 'text-xs' : 'text-sm'} transition-all
                      ${selectedPaymentMethod === method.id 
                        ? "bg-blue-50 text-blue-600 font-medium" 
                        : "text-gray-600 hover:text-gray-800"}
                    `}
                  >
                    <method.icon className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    <span>{method.name}</span>
                  </button>
                ))}
              </div>
              
              {/* Trust Badges - More visible for reassurance */}
              <div className="flex gap-3 flex-wrap justify-center">
                {TRUST_MARKS.map((mark, idx) => (
                  <span key={idx} className={`flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>
                    <mark.icon className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${mark.color}`} />
                    {mark.text}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Payment Logos and Guarantee - More professional layout */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-2 pt-2 border-t border-gray-200">
              <div className="flex gap-2 mb-2 sm:mb-0">
                <img src="/visa.png" alt="Visa" className={`${isMobile ? 'h-5' : 'h-6'}`} />
                <img src="/mastercard.jpg" alt="Mastercard" className={`${isMobile ? 'h-5' : 'h-6'}`} />
                <img src="/american.svg" alt="American Express" className={`${isMobile ? 'h-5' : 'h-6'}`} />
                <img src="/oxxo.webp" alt="OXXO" className={`${isMobile ? 'h-5' : 'h-6'}`} />
              </div>
              
              <div className="flex items-center gap-2">
                {/* <div className={`flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 bg-white px-2 py-1 rounded-full border border-gray-200 shadow-sm`}>
                  <Shield className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-green-600`} />
                  <span>Garantía de devolución de 14 días</span>
                </div> */}
                
                {plan !== "Free" && (
                  <button
                    onClick={handleSubscriptionManagement}
                    className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-600 hover:text-blue-800 hover:underline font-medium`}
                  >
                    Gestionar suscripción
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PricingModal;